// Norwegian land-use plan codes, read the same way as the backend
// (backend/app/services/norway/plan_codes.py). The data file is a copy of
// backend/data/plan_codes_no.json, built from the official Geonorge
// registers; a backend test fails when the two copies differ.
//
// DIBK's planning service answers with three code generations and the same
// number means different things in each (PBL 2008 4-digit arealformål,
// PBL 1985 3-digit reguleringsformål, PBL 1985 3-digit arealbruk). Codes are
// translated into the PBL 2008 space; the official name of the source code is
// kept for display.
import DATA from '../data/plan_codes_no.json';

export const PLAN_SCHEME = DATA.scheme;
export const INDUSTRY_GRADE = DATA.suitability.industry_grade;

const NAMES_2008 = (() => {
  const out = {};
  for (const [k, v] of Object.entries(DATA.lists.kp_arealformal)) out[k] = v.name;
  for (const [k, v] of Object.entries(DATA.lists.rp_arealformal)) out[k] = v.name;
  return out;
})();

const digits = (code) => {
  if (code === null || code === undefined) return null;
  let s = String(code).trim();
  if (s.endsWith('.0')) s = s.slice(0, -2);
  return /^\d+$/.test(s) ? s : null;
};

export function planCodeName(code) {
  const s = digits(code);
  if (!s) return null;
  const n = parseInt(s, 10);
  for (const cand of [s, String(Math.floor(n / 10) * 10), String(Math.floor(n / 100) * 100), String(Math.floor(n / 1000) * 1000 + 1)]) {
    if (NAMES_2008[cand]) return NAMES_2008[cand];
  }
  return null;
}

export function normalisePlanCode(code, generation = 'pbl2008') {
  const s = digits(code);
  if (!s) return null;
  let gen = generation;
  if (gen === 'pbl2008' && s.length === 3) gen = 'pbl1985_reg';
  if (gen === 'pbl1985_reg' || gen === 'pbl1985_kp') {
    const key = gen === 'pbl1985_reg' ? 'pbl1985_reguleringsformal' : 'pbl1985_arealbruk';
    const table = DATA[`${key}_to_2008`];
    const n = parseInt(s, 10);
    const mapped = table[s] || table[String(Math.floor(n / 10) * 10)] || table[String(Math.floor(n / 100) * 100)];
    if (!mapped) return null;
    const src = DATA.lists[key][s] || {};
    const plan = gen === 'pbl1985_reg' ? 'reguleringsplan' : 'kommuneplan';
    const label = src.name || planCodeName(mapped) || `Formål ${s}`;
    return { code: mapped, name: `${label} (${plan} etter pbl 1985, formål ${s})`, sourceCode: s, generation: gen };
  }
  return { code: s, name: planCodeName(s) || `Formål ${s}`, sourceCode: s, generation: 'pbl2008' };
}

// One GetFeatureInfo feature's properties → { code, name, sourceCode, generation } | null
export function readPlanCode(props, isReguleringsplan = false) {
  if (!props) return null;
  const otype = String(props.objekttypenavn || '');
  const reg = props['reguleringsformål'] ?? props['reguleringsFormål'];
  let areal = null;
  for (const k of ['arealformål', 'arealFormål', 'arealFormålKode', 'arealformaalKode', 'formål', 'formaalKode']) {
    if (props[k] !== undefined && props[k] !== null && props[k] !== '') { areal = props[k]; break; }
  }
  const bruk = props.arealbruk;
  let out = null;
  if (reg !== undefined && reg !== null && reg !== '' && (otype.startsWith('Rb') || areal === null)) {
    out = normalisePlanCode(reg, 'pbl1985_reg');
  }
  if (!out && areal !== null) {
    const s = digits(areal);
    const gen = s && s.length === 3
      ? ((otype.includes('Arealbruk') || !isReguleringsplan) ? 'pbl1985_kp' : 'pbl1985_reg')
      : 'pbl2008';
    out = normalisePlanCode(areal, gen);
  }
  if (!out && bruk !== undefined && bruk !== null && bruk !== '') out = normalisePlanCode(bruk, 'pbl1985_kp');
  if (!out) {
    for (const k of ['feltbetegnelse', 'områdenavn']) {
      const v = props[k];
      if (!v) continue;
      const token = String(v).trim().toUpperCase().split(/[^A-ZÆØÅ]/)[0];
      // single letters are plain field labels in old plans (A, B, C…)
      if (token.length >= 2 && DATA.letter_codes[token]) {
        out = normalisePlanCode(DATA.letter_codes[token], 'pbl2008');
        if (out) out.name += ` (fra feltnavn ${v})`;
        break;
      }
    }
  }
  if (!out) return null;
  const desc = String(props.beskrivelse || '').trim();
  if (desc && desc.length <= 60 && !out.name.toLowerCase().includes(desc.toLowerCase())) out.name += ` – ${desc}`;
  return out;
}

const to2008 = (code) => {
  const s = digits(code);
  if (!s) return null;
  if (s.length === 3) { const r = normalisePlanCode(s, 'pbl1985_reg'); return r ? r.code : null; }
  return s;
};

export function planCodeSuitability(code) {
  if (digits(code) === null) return null;
  const s = to2008(code);
  const t = DATA.suitability;
  if (s === null) return t.default;
  if (t.exact[s] !== undefined) return t.exact[s];
  const n = parseInt(s, 10);
  for (const [lo, hi, v] of t.ranges) if (n >= lo && n <= hi) return v;
  return t.default;
}

export const isIndustryGrade = (code) => {
  const v = planCodeSuitability(code);
  return v !== null && v >= INDUSTRY_GRADE;
};

export function planCodeCategory(code) {
  const s = to2008(code);
  if (s === null) return 'unknown';
  const t = DATA.category;
  if (t.exact[s]) return t.exact[s];
  const n = parseInt(s, 10);
  for (const [lo, hi, c] of t.ranges) if (n >= lo && n <= hi) return c;
  return 'unknown';
}
